import {useContext} from 'react';
import cx from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {Button} from '~library/atoms/Button';
import {
  selectActiveCardEditor,
  selectCurrentSection,
  selectLoaded,
  selectPremiumFeaturesPristine,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {DiamondFillIcon} from '~library/svg/DiamondFillIcon';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {canvasRemoveTextBordersAndIcons} from '~/common/canvas/customs/text_border';

import './NavButton.sass';

export const NavButton = ({
  title,
  icon,
  section,
  editor = '',
  disableAutoOpenEditor,
  premiumFeature = false,
  isPremium = false,
}) => {
  const {canvas, processing} = useContext(EventCreateContext);
  const currentSection = useSelector(selectCurrentSection);
  const loaded = useSelector(selectLoaded);
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const isMobile = !isDesktop;
  const navigate = useNavigator({isMobile, disableAutoOpenEditor});
  const dispatch = useDispatch();
  const premiumFeaturesPristine = useSelector(selectPremiumFeaturesPristine);
  const activeCardEditor = useSelector(selectActiveCardEditor);

  const sectionTitle = typeof title === 'string' ? [title] : title;
  const onClickHandler = () => {
    if (processing) return;

    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {
        activeCardEditor: editor,
      },
    });

    // Remove text borders when navigating away from card canvas editor
    if (['enveloper_line', 'stamp', 'background', 'reveal'].includes(section)) {
      canvasRemoveTextBordersAndIcons(canvas);
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          shouldTogglePremiumBackground: true,
        },
      });
    } else if (section === 'card_design') {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          editorDisabled: true,
          shouldTogglePremiumBackground: false,
        },
      });
    }

    if (section !== Sections.CARD_DESIGN && section !== Sections.UPLOAD_PHOTO) {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          usedPremiumFeature: true,
        },
      });
    } else if (premiumFeaturesPristine) {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          usedPremiumFeature: false,
        },
      });
    }
    navigate(section);
  };

  const activeClass = () => {
    if (editor) {
      return editor === activeCardEditor ? 'create_flow__nav_bottom__section--active' : '';
    }
    return currentSection === section ? 'create_flow__nav_bottom__section--active' : '';
  };

  const activeButtonClass = () => {
    if (editor) {
      return editor === activeCardEditor ? 'active' : '';
    }
    return currentSection === section ? 'active' : '';
  };

  return (
    <div
      className={cx(`create_flow__nav_bottom__section ${activeClass()}`, {
        'nav-button--loading': processing,
      })}
      onClick={onClickHandler}
      role="button"
      data-qa-id={`nav-bottom-${section}`}
    >
      <div className="create_flow__nav_bottom__section__button-wrapper">
        <Button
          id="navBottom"
          className={`create_flow__nav_bottom__section__button ${
            !isPremium && premiumFeature
              ? 'create_flow__nav_bottom__section__button--premium-preview'
              : ''
          } ${activeButtonClass()}`}
          variant="secondary"
          size="small"
          disabled={!loaded}
          data-qa-id={`nav-bottom-${section}-button`}
        >
          {icon}
        </Button>
        {!isPremium && premiumFeature && (
          <DiamondFillIcon className="premium-diamond" ratio={isDesktop ? 0.3 : 0.25} />
        )}
      </div>
      <div className="create_flow__nav_bottom__section__title__wrapper">
        {sectionTitle.map((rowTitle, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <p key={`${rowTitle}-${index}`} className="create_flow__nav_bottom__section__title">
            {rowTitle}
          </p>
        ))}
      </div>
    </div>
  );
};
